import { useCallback, useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { Button, SearchForm, TableContainer, TableHeader } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { PageSection } from '@occc/ui-design-system';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import {
  RetailerNetworkFragment,
  RetailerNetworkUpdate,
  RetailerNetworkUpdateMutationOptions,
  RetailerNetworkUserCreate,
  Retailer_Network_User_Role,
  User_Role,
} from '@occc/graphql';
import { Can } from '@occc-app/App/PolicyBasedAccessControl';
import {
  CreateRetailerNetworkUser,
  UpdateRetailerNetwork,
} from '@occc-app/App/PolicyBasedAccessControl/userActions';
import config from '@occc/config';

import { RetailerNetworkUsersTable } from './retailer-network-users-table.component';
import { RetailerNetworkAddNewUserDialog } from './components/retailer-network-add-new-user-dialog.component';
import { RetailerNetworkAddExistingUserDialog } from './components/retailer-network-add-existing-user-dialog.component';
import { NewRetailerNetworkUserFormValues } from './retailer-network-users-configuration.types';

import { useRetailerNetworkUserCreateMutation } from '../../data-access/mutations';
import { useGetRetailerNetworkUsers } from '../../data-access/queries';
import { routeCreator } from '../retailer-connect.utils';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';

const PAGE_TITLE = 'Network Users';

type RetailerNetworkUsersConfigurationContainerProps = {
  retailerNetwork: RetailerNetworkFragment;
  retailerNetworkUpdate: (
    input: Omit<RetailerNetworkUpdate, 'uuid'>,
    refetchQueries: RetailerNetworkUpdateMutationOptions['refetchQueries'],
  ) => Promise<void>;
  showNavigationButtons?: boolean;
  variant?: 'standalone' | 'section';
};

export const RetailerNetworkUsersConfigurationContainer = ({
  retailerNetwork,
  retailerNetworkUpdate: retailerNetworkUpdateMutation,
  showNavigationButtons,
  variant = 'standalone',
}: RetailerNetworkUsersConfigurationContainerProps) => {
  useBreadcrumbLinks();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [paginationInfo, onPageChange] = usePaginationParams();

  const [addNewUserDialogOpen, setAddNewUserDialogOpen] = useState<boolean>(false);
  const [addExistingUserDialogOpen, setAddExistingUserDialogOpen] = useState<boolean>(false);

  const handleAddNewUserDialogOpen = useCallback(() => {
    setAddNewUserDialogOpen(!addNewUserDialogOpen);
  }, [setAddNewUserDialogOpen, addNewUserDialogOpen]);
  const handleAddExistingUserDialogOpen = useCallback(() => {
    setAddExistingUserDialogOpen(!addExistingUserDialogOpen);
  }, [setAddExistingUserDialogOpen, addExistingUserDialogOpen]);

  const retailerNetworkUpdate = async (newRetailerNetworkUsers: RetailerNetworkUserCreate[]) => {
    await retailerNetworkUpdateMutation({ newRetailerNetworkUsers }, ['retailerNetworkUsers']);
  };

  const [retailerNetworkUserCreateMutation] = useRetailerNetworkUserCreateMutation({
    onCompleted: () =>
      snackbar.success('User has been successfully created and added to the network'),
    refetchQueries: ['retailerNetworkUsers'],
  });

  const handleRetailerNetworkUserCreate = useCallback(
    async (values: NewRetailerNetworkUserFormValues) => {
      await retailerNetworkUserCreateMutation({
        variables: {
          input: {
            openCommerceUser: {
              name: {
                first: values.firstName,
                last: values.lastName,
              },
              email: values.email.trim(),
              userRole: User_Role.RcUser,
              phoneNumber: values.phoneNumber ? values.phoneNumber.trim() : undefined,
            },
            retailerNetworkUuid: retailerNetwork.uuid,
            role: values.userRole,
          },
        },
      });
      handleAddNewUserDialogOpen();
    },
    [retailerNetworkUserCreateMutation, retailerNetwork.uuid, handleAddNewUserDialogOpen],
  );

  const handleRedirectToUserDetailsPage = useCallback(
    (userUuid: string) => {
      navigate(routeCreator.userDetails(retailerNetwork.uuid, userUuid));
    },
    [navigate, retailerNetwork.uuid],
  );

  const { loading, error, data, paginationProps } = useGetRetailerNetworkUsers({
    filter: {
      retailerNetworkUuidEquals: retailerNetwork.uuid,
      nameLike: search,
      roleIn: [
        Retailer_Network_User_Role.RetailerNetworkAdmin,
        Retailer_Network_User_Role.RetailerNetworkUser,
      ],
    },
    paginationInfo,
    onPageChange,
  });

  const tableActions = useMemo(() => {
    return (
      <Stack direction='row' spacing={2} justifyContent='flex-end'>
        <Box sx={{ maxWidth: 400, width: '100%' }}>
          <SearchForm
            key='search'
            onSubmit={(values) => {
              setSearch(values.search);
            }}
            initialValues={{ search }}
          />
        </Box>
        <Can I={UpdateRetailerNetwork({ uuid: retailerNetwork.uuid })}>
          {config.retailerConnect.addExistingUserEnabled && (
            <Button
              key='addExistingUserButton'
              color='primary'
              variant='contained'
              onClick={handleAddExistingUserDialogOpen}
              role='button'
              sx={{ minWidth: 'fit-content' }}
            >
              Add Existing User
            </Button>
          )}
          <Can I={CreateRetailerNetworkUser()}>
            <Button
              key='addNewUserButton'
              color='primary'
              variant='contained'
              onClick={handleAddNewUserDialogOpen}
              role='button'
              sx={{ minWidth: 'fit-content' }}
            >
              Create a New User
            </Button>
          </Can>
        </Can>
      </Stack>
    );
  }, [
    search,
    setSearch,
    handleAddExistingUserDialogOpen,
    handleAddNewUserDialogOpen,
    retailerNetwork.uuid,
  ]);

  const usersTable = (
    <RetailerNetworkUsersTable
      retailerNetwork={retailerNetwork}
      data={data}
      loading={loading}
      error={error}
      paginationProps={paginationProps}
      isFilterApplied={Boolean(search)}
      handleRedirectToUserDetailsPage={handleRedirectToUserDetailsPage}
    />
  );

  return (
    <>
      {variant === 'section' ? (
        <PageSection title={PAGE_TITLE} actions={tableActions}>
          {usersTable}
        </PageSection>
      ) : (
        <TableContainer>
          <TableHeader title={PAGE_TITLE} actions={tableActions} />
          {usersTable}
        </TableContainer>
      )}
      {/* TODO: move navigation buttons to the config container */}
      {showNavigationButtons && (
        <Box m={1} display='flex' justifyContent='flex-end' alignItems='flex-end' gap={2}>
          <Button
            variant='text'
            color='primary'
            onClick={() =>
              navigate(
                retailerNetwork?.uuid
                  ? routeCreator.overview(retailerNetwork.uuid)
                  : routeCreator.base(),
              )
            }
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => navigate(routeCreator.overview(retailerNetwork.uuid))}
            data-testid='retailer-network-configuration-button'
          >
            Continue
          </Button>
        </Box>
      )}
      {addNewUserDialogOpen && (
        <RetailerNetworkAddNewUserDialog
          open={addNewUserDialogOpen}
          onClose={handleAddNewUserDialogOpen}
          onSubmit={handleRetailerNetworkUserCreate}
        />
      )}
      {addExistingUserDialogOpen && (
        <RetailerNetworkAddExistingUserDialog
          open={addExistingUserDialogOpen}
          onClose={handleAddExistingUserDialogOpen}
          retailerNetworkUpdate={retailerNetworkUpdate}
        />
      )}
    </>
  );
};
